/**
 * DraftDecoupled applier.
 *
 * Takes tile from tileSlot and wildlife token from wildlifeSlot (may be the
 * same slot). Empties both slots. Advances phase: marketCheck → placeTile.
 *
 * Nature token was already deducted by SpendNatureDecouple. This applier does
 * NOT deduct it again.
 *
 * Spec §6.3.
 */

import type { DraftDecoupledAction } from "../types/actions";
import type { GameState } from "../types/game-state";
import type { MarketSlot } from "../types/market";
import { bumpVersion } from "./common-applier";

export const applyDraftDecoupled = (
  state: GameState,
  action: DraftDecoupledAction,
): GameState => {
  const tileSlot = state.market.slots[action.tileSlot];
  const wildlifeSlot = state.market.slots[action.wildlifeSlot];

  const draftedTile = tileSlot.tileInstanceId;
  const draftedToken = wildlifeSlot.tokenInstanceId;

  // Empty both slots (if tileSlot === wildlifeSlot, one pass clears both fields)
  const newSlots = state.market.slots.map((s) => {
    if (s.index === action.tileSlot && s.index === action.wildlifeSlot) {
      return {
        ...s,
        tileInstanceId: null,
        tokenInstanceId: null,
        tileDefId: null,
        wildlifeSpecies: null,
      } satisfies MarketSlot;
    }
    if (s.index === action.tileSlot) {
      return {
        ...s,
        tileInstanceId: null,
        tileDefId: null,
      } satisfies MarketSlot;
    }
    if (s.index === action.wildlifeSlot) {
      return {
        ...s,
        tokenInstanceId: null,
        wildlifeSpecies: null,
      } satisfies MarketSlot;
    }
    return s;
  }) as [MarketSlot, MarketSlot, MarketSlot, MarketSlot];

  const activeSeat = state.turn.activeSeat;

  return {
    ...state,
    ...bumpVersion(state),
    market: {
      ...state.market,
      slots: newSlots,
    },
    turn: {
      ...state.turn,
      phase: "placeTile",
    },
    players: state.players.map((p) =>
      p.seat === activeSeat
        ? {
            ...p,
            draft: {
              tileInstanceId: draftedTile,
              tokenInstanceId: draftedToken,
            },
          }
        : p,
    ),
  };
};
